/**
 * Pure helper for building the Firestore document body of a
 * per-member `AssignedChore` (the fan-out doc written under
 * `member_profiles/{uid}/assigned_chores/`). Mirrors iOS
 * `LocalDataStore.saveAssignedChore` and Android
 * `ZoeyRepository.saveAssignedChore` (zoey_android commit `ac3722d`).
 *
 * The fields copied here matter at chore-completion time: the
 * `chore_completions` writer in `lib/chores.ts:markChoreComplete`
 * reads `chore.points ?? 0` and `chore.tip_points ?? 0` from the
 * assigned-chore doc, so any field dropped here silently becomes
 * `points_earned: 0` in the event log → wallet aggregator never
 * accumulates. The bug shipped on Android before commit `ac3722d`
 * (chore_completions events with zero points) was exactly this drop.
 *
 * See [[tasks/daily-chore-reset]].
 */

import { Timestamp } from "firebase/firestore";

export interface BuildAssignedChoreInput {
  /** Parent household chore. Source of truth for `title`, `description`,
   *  `points`, `category`, `due_date`, `recurrence_type`. */
  chore: {
    uid?: string | null;
    title: string;
    description?: string | null;
    points?: number | null;
    category?: string | null;
    due_date?: unknown; // Timestamp | undefined; opaque here so we don't import full models
    recurrence_type?: string | null;
  };
  /** Active household id — denormalized onto the assigned-chore doc so
   *  the active-household filter on the reading side can match. */
  householdId: string;
  /** Who created the assignment (typically the current user's uid). */
  assignedBy: string;
}

export interface AssignedChoreDoc {
  assigned_at: Timestamp;
  assigned_by: string;
  household_id: string;
  title: string;
  description: string | null;
  due_date: unknown;
  recurrence: string | null;
  root_chore_uid: string | null;
  isCompleted: false;
  points: number | null;
  category: string | null;
}

export function buildAssignedChoreDoc(
  input: BuildAssignedChoreInput,
): AssignedChoreDoc {
  const { chore, householdId, assignedBy } = input;
  return {
    assigned_at: Timestamp.now(),
    assigned_by: assignedBy,
    household_id: householdId,
    title: chore.title,
    description: chore.description ?? null,
    due_date: chore.due_date ?? null,
    recurrence: chore.recurrence_type ?? null,
    root_chore_uid: chore.uid ?? null,
    isCompleted: false,
    points: chore.points ?? null,
    category: chore.category ?? null,
  };
}
